import { useEffect, useMemo, useState } from 'react';
import { buildMtfV2ApiUrl, ensureArray, getApiHeaders } from './config';
import { getAuthProfileFallback, getAuthToken, getAuthUserKey, isAdmin } from './auth/authUtils';

export const RULE_SYMBOLS_EVENT = 'signalfly-rule-symbols-change';

const storageKey = workspace => `signalfly.rule-symbols.v1:${workspace.exchange}:${workspace.marketType}`;

export function saveRuleSymbolOutcome(workspace, symbols, ruleName = '') {
  const value = { symbols: [...new Set(ensureArray(symbols).filter(Boolean))].sort(), ruleName, updatedAt: new Date().toISOString() };
  localStorage.setItem(storageKey(workspace), JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(RULE_SYMBOLS_EVENT, { detail: { workspace, value } }));
}

function readRuleOutcome(workspace) {
  try { return JSON.parse(localStorage.getItem(storageKey(workspace))) || { symbols: [] }; }
  catch { return { symbols: [] }; }
}

export default function useDashboardSymbolScope(workspace) {
  const profile = getAuthProfileFallback();
  const premiumEligible = isAdmin() || Boolean(profile?.subscriptionActive && String(profile?.plan).toUpperCase() === 'PREMIUM');
  const [mode, setMode] = useState('ALL');
  const [ruleOutcome, setRuleOutcome] = useState(() => readRuleOutcome(workspace));
  const [interestedSymbols, setInterestedSymbols] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setMode('ALL'); setRuleOutcome(readRuleOutcome(workspace));
    const refresh = () => setRuleOutcome(readRuleOutcome(workspace));
    window.addEventListener(RULE_SYMBOLS_EVENT, refresh); window.addEventListener('storage', refresh);
    return () => { window.removeEventListener(RULE_SYMBOLS_EVENT, refresh); window.removeEventListener('storage', refresh); };
  }, [workspace]);

  useEffect(() => { if (!premiumEligible && mode === 'RULE') setMode('ALL'); }, [premiumEligible, mode]);

  useEffect(() => {
    if (mode !== 'RULE') return undefined;
    const controller = new AbortController(); const token = getAuthToken(); const userKey = getAuthUserKey();
    setError('');
    fetch(buildMtfV2ApiUrl('/api/bot/ui/rulesets', workspace), { signal:controller.signal, headers:getApiHeaders({ ...(token ? { Authorization:`Bearer ${token}` } : {}), ...(userKey ? { 'X-SIGNALFLY-USER':userKey } : {}) }) })
      .then(async response => { if (!response.ok) throw new Error(`Unable to load presets (${response.status})`); return response.json(); })
      .then(value => { const next = ensureArray(value); setPresets(next); setSelectedPresetId(current => current || String(next.find(item => item.name === ruleOutcome.ruleName)?.id || next[0]?.id || '')); })
      .catch(reason => { if (reason.name !== 'AbortError') setError(reason.message); });
    return () => controller.abort();
  }, [mode, workspace, ruleOutcome.ruleName]);

  const applySelectedPreset = async () => {
    const preset = presets.find(item => String(item.id) === String(selectedPresetId)); if (!preset) return;
    setApplying(true); setError('');
    try {
      const response = await fetch(buildMtfV2ApiUrl('/api/bot/ui/rule-filter/evaluate', workspace), { method:'POST', headers:getApiHeaders({ 'Content-Type':'application/json', Authorization:`Bearer ${getAuthToken()}` }), body:JSON.stringify(preset.definition) });
      if (!response.ok) throw new Error(`Unable to apply preset (${response.status})`);
      const result = await response.json(); const nextSymbols = ensureArray(result.qualifiedSymbols);
      saveRuleSymbolOutcome(workspace, nextSymbols, preset.name); setRuleOutcome(readRuleOutcome(workspace));
    } catch (reason) { setError(reason.message || 'Unable to apply preset'); }
    finally { setApplying(false); }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetch(buildMtfV2ApiUrl('/api/bot/ui/get/upcoming-plan/getInterestedSymbols', workspace), { headers: getApiHeaders(), signal: controller.signal })
      .then(response => response.ok ? response.json() : [])
      .then(value => setInterestedSymbols([...new Set(ensureArray(value).filter(Boolean))].sort()))
      .catch(error => { if (error.name !== 'AbortError') setInterestedSymbols([]); });
    return () => controller.abort();
  }, [workspace]);

  const symbols = useMemo(() => mode === 'RULE' ? ensureArray(ruleOutcome.symbols) : mode === 'INTERESTED' ? interestedSymbols : [], [mode, ruleOutcome, interestedSymbols]);
  return { mode, setMode, symbols, active: mode !== 'ALL', premiumEligible, ruleOutcome, interestedSymbols, presets, selectedPresetId, setSelectedPresetId, applySelectedPreset, applying, error };
}
